import { useEffect, useState } from "react";
import { ExternalLink, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function SynergySection({ slug }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!slug) return;
        api.get(`/products/${slug}/synergy`)
            .then(({ data }) => setData(data))
            .catch(() => setData(null));
    }, [slug]);

    // Hidden until a public affiliate or partner item is attached.
    if (!data || (!data.affiliate && !data.digital)) return null;

    const { base, affiliate, digital, bundle_subtotal, split_preview } = data;

    const onOneClickBundle = async () => {
        setBusy(true);
        try {
            const { data: res } = await api.post("/checkout/bundle", {
                base_slug: base.slug,
                include_digital: !!digital,
                digital_slug: digital?.slug,
                donate: true,
                origin_url: window.location.origin,
            });
            if (res?.url) {
                window.location.href = res.url;
            } else {
                toast.error("Bundle checkout unavailable");
            }
        } catch (e) {
            const msg = e?.response?.data?.detail || "Bundle checkout failed";
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <section
            className="bg-cream-warm py-16 md:py-20 border-t border-forest/10"
            data-testid="synergy-section"
        >
            <div className="max-w-7xl mx-auto px-5 md:px-10">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
                    <div>
                        <span className="divider-rule text-brown">
                            <Sparkles size={12} /> Synergy · Island Pairing
                        </span>
                        <h2 className="font-serif text-3xl sm:text-4xl text-forest mt-3 leading-[1.05]">
                            Pairs naturally with —
                        </h2>
                    </div>
                    <div
                        className="text-[0.65rem] tracking-[0.3em] uppercase text-brown/70"
                        data-testid="synergy-split-preview"
                    >
                        Bundle split · {split_preview.cyberkids_pct}% CyberKids ·{" "}
                        {split_preview.director_pct}% Director ·{" "}
                        {split_preview.pairing_pool_pct}% Pairing Pool
                    </div>
                </div>

                <div className="grid md:grid-cols-3 gap-5">
                    {/* Base */}
                    <div
                        className="p-5 bg-cream rounded-sm border border-forest/10"
                        data-testid="synergy-base-card"
                    >
                        <div className="aspect-square overflow-hidden rounded-sm bg-cream-warm">
                            <img
                                src={base.image}
                                alt={base.name}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="mt-4 text-[0.6rem] tracking-[0.3em] uppercase text-gold">
                            Base · {base.category}
                        </div>
                        <h3 className="font-serif text-xl text-forest mt-1">
                            {base.name}
                        </h3>
                        <p className="text-sm text-brown mt-1">
                            ${base.price.toFixed(2)}
                        </p>
                    </div>

                    {/* Affiliate (optional) */}
                    {affiliate ? (
                        <div
                            className="p-5 bg-cream rounded-sm border border-forest/10"
                            data-testid="synergy-affiliate-card"
                        >
                            <div className="aspect-square overflow-hidden rounded-sm bg-cream-warm">
                                <img
                                    src={affiliate.image}
                                    alt={affiliate.name}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="mt-4 text-[0.6rem] tracking-[0.3em] uppercase text-gold">
                                Affiliate · {affiliate.category_label}
                            </div>
                            <h3 className="font-serif text-xl text-forest mt-1">
                                {affiliate.name}
                            </h3>
                            <p className="text-sm text-brown mt-1">
                                {affiliate.vendor} · ${affiliate.price?.toFixed?.(2) ?? affiliate.price}
                            </p>
                            <a
                                href={affiliate.buy_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-3 inline-flex items-center gap-2 text-[0.65rem] tracking-[0.3em] uppercase text-forest hover:text-gold"
                                data-testid="synergy-affiliate-link"
                            >
                                Visit vendor <ExternalLink size={12} />
                            </a>
                        </div>
                    ) : (
                        <div
                            className="p-5 bg-cream rounded-sm border border-dashed border-forest/20 flex items-center justify-center text-center"
                            data-testid="synergy-affiliate-empty"
                        >
                            <p className="text-xs tracking-[0.25em] uppercase text-brown/60">
                                Scout log empty —<br />no affiliate pairing yet
                            </p>
                        </div>
                    )}

                    {/* Digital */}
                    {digital && (
                        <div
                            className="p-5 bg-forest text-cream rounded-sm"
                            data-testid="synergy-digital-card"
                        >
                            <div className="aspect-square overflow-hidden rounded-sm bg-forest-deep">
                                <img
                                    src={digital.image}
                                    alt={digital.name}
                                    className="w-full h-full object-cover opacity-90"
                                />
                            </div>
                            <div className="mt-4 text-[0.6rem] tracking-[0.3em] uppercase text-gold">
                                Digital · {digital.kind}
                            </div>
                            <h3 className="font-serif text-xl mt-1">
                                {digital.name}
                            </h3>
                            <p className="text-sm text-cream/70 mt-1">
                                {digital.tagline} · ${digital.price.toFixed(2)}
                            </p>
                        </div>
                    )}
                </div>

                <div className="mt-10 flex flex-wrap items-center justify-between gap-5 p-6 bg-forest text-cream rounded-sm">
                    <div>
                        <span className="text-[0.6rem] tracking-[0.3em] uppercase text-gold">
                            One-Click Island Bundle
                        </span>
                        <p className="font-serif text-2xl mt-2">
                            ${bundle_subtotal.toFixed(2)}{" "}
                            <span className="text-cream/60 text-sm">
                                + 10% CyberKids round-up at checkout
                            </span>
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onOneClickBundle}
                        disabled={busy}
                        className="btn-pill btn-gold disabled:opacity-60"
                        data-testid="synergy-one-click-bundle-btn"
                    >
                        {busy ? (
                            <>
                                <Loader2 size={14} className="animate-spin" />{" "}
                                Routing…
                            </>
                        ) : (
                            <>
                                <Sparkles size={14} /> Get the Bundle
                            </>
                        )}
                    </button>
                </div>
            </div>
        </section>
    );
}
